import React, { memo, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { createUseStyles, Icon, useTheme, Tooltip } from '@Sage/hoi-poi-ui';
import classnames from 'classnames';
import { useWeb5Context } from "../../../../../lib/Web5Context";
import styles from "./styles";
const useStyles = createUseStyles(styles, 'CopilotSpeechButton');
const SpeechButton = /*#__PURE__*/memo(_ref => {
  let {
    editor,
    locale,
    isDisabled,
    setIsListening,
    isListening
  } = _ref;
  const classes = useStyles();
  const {
    t
  } = useWeb5Context();
  const [isOver, setIsOver] = useState(false);
  const recognitionRef = useRef(null);
  const isFirstRender = useRef(true);
  const theme = useTheme();
  const isSpeechRecognitionSupported = 'SpeechRecognition' in window || 'webkitSpeechRecognition' in window;
  useEffect(() => {
    if (isFirstRender !== null && isFirstRender !== void 0 && isFirstRender.current) {
      isFirstRender.current = false;
      if (!isSpeechRecognitionSupported) return;
      recognitionRef.current = new window.webkitSpeechRecognition(); //|| new SpeechRecognition();
      recognitionRef.current.lang = locale || 'es-Es'; // Set language
      recognitionRef.current.interimResults = true; // Returns partial results so you see the writting effect
    }
  }, [locale, isSpeechRecognitionSupported]);
  const startListening = useCallback(() => {
    recognitionRef.current.onstart = () => {
      setIsListening(true);
    };
    recognitionRef.current.onresult = event => {
      const transcript = event.results[0][0].transcript;
      const textCapitalized = transcript.charAt(0).toUpperCase() + transcript.slice(1);
      if (editor !== null && editor !== void 0 && editor.commands) {
        var _editor$callbacks, _editor$callbacks$upd;
        editor.commands.setContent(textCapitalized);
        const onUpdate = (editor === null || editor === void 0 ? void 0 : (_editor$callbacks = editor.callbacks) === null || _editor$callbacks === void 0 ? void 0 : (_editor$callbacks$upd = _editor$callbacks.update) === null || _editor$callbacks$upd === void 0 ? void 0 : _editor$callbacks$upd[0]) || null;
        if (onUpdate) onUpdate({
          editor
        });
      }
    };
    recognitionRef.current.onend = props => {
      setIsListening(false);
      if (editor !== null && editor !== void 0 && editor.commands) editor.commands.focus();
    };
    recognitionRef.current.start();
  }, [editor, setIsListening]);
  const stopListening = useCallback(() => {
    setIsListening(false);
    recognitionRef.current.abort();
  }, [setIsListening]);
  const handleOnClick = useCallback(() => {
    if (isDisabled) return null;
    if (isListening) return stopListening();
    return startListening();
  }, [isDisabled, isListening, startListening, stopListening]);
  const displayMicOutline = useMemo(() => {
    if (isListening) return false;
    if (isDisabled) return true;
    if (!isOver) return true;
  }, [isListening, isDisabled, isOver]);
  const displayMic = useMemo(() => {
    if (!displayMicOutline && !isListening) return true;
    return false;
  }, [displayMicOutline, isListening]);
  const onMouseOver = useCallback(e => {
    e.stopPropagation();
    setIsOver(true);
  }, []);
  const onMouseOut = useCallback(e => {
    e.stopPropagation();
    setIsOver(false);
  }, []);
  if (!isSpeechRecognitionSupported) return null;
  return /*#__PURE__*/React.createElement(Tooltip, {
    placement: "topRight",
    content: /*#__PURE__*/React.createElement("span", {
      style: {
        whiteSpace: 'nowrap'
      }
    }, t('label_speak'))
  }, /*#__PURE__*/React.createElement("div", {
    className: classnames(classes.root, {
      [classes.disabled]: isDisabled
    }),
    onClick: handleOnClick,
    onMouseOver: onMouseOver,
    onMouseOut: onMouseOut
  }, displayMicOutline && /*#__PURE__*/React.createElement(Icon, {
    name: "micOutline",
    size: "large",
    color: !isDisabled ? theme.colors.actionMajor[500] : theme.colors.actionMinor[500]
  }), displayMic && /*#__PURE__*/React.createElement(Icon, {
    name: "mic",
    size: "large",
    color: !isDisabled ? theme.colors.actionMajor[500] : theme.colors.actionMinor[500]
  }), isListening && /*#__PURE__*/React.createElement(Icon, {
    name: "stopRecording",
    size: "large",
    color: theme.colors.semantic.info500
  })));
});
export default SpeechButton;